import { BadgeCheck, AlertTriangle } from "lucide-react";
import { observer } from "mobx-react";
import { Card, CardContent, CardHeader, CardTitle, Preloader } from 'randmarcomps';
import { ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState, useId } from "react";
import { useParams } from "react-router-dom";
import SalesHistoryChart from "../components/charts/SalesHistoryChart";
import OpenOrdersGrid from "../components/grids/OpenOrdersGrid";
import OpportunitiesGrid from '../components/grids/OpportunitiesGrid';
import ReturnGrid from "../components/grids/ReturnGrid";
import ResellerActions from "../components/reseller/ResellerActions";
import ButtonWithActions from "../components/util/ButtonWithActions";
import OpenOrdersSummary from "../componentsCore/cards/OpenOrdersSummary";
import RampUIResellerCard from "../componentsCore/cards/RampUIResellerCard";
import SalesSummary from "../componentsCore/cards/SalesSummary";
import ManufacturerContext from "../contexts/ManufacturerContext";
import { RootStoreContext } from "../stores/rootStore";
import withRoot from "../withRoot";

const sectionIds = {
  activeOrders: "reseller-active-orders",
  completedOrders: "reseller-completed-orders",
  returns: "reseller-returns",
  opportunities: "reseller-opportunities",
  qualifications: "reseller-qualifications",
};

const formatDate = (value?: number) => {
  if (!value) return "No activity yet";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No activity yet";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const normalizeUrl = (url?: string) => {
  if (!url) return undefined;
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

type ContactItem = {
  label: string;
  value: string;
  type: "text" | "link" | "action";
  href?: string;
};

const ResellerPage: React.FC = observer(() => {
  const { applicationId } = useContext(ManufacturerContext);
  const [loading, setLoading] = useState(true);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [highlightedQualification, setHighlightedQualification] = useState<string | null>(null);
  const store = useContext(RootStoreContext);
  const { resellerId } = useParams<{ resellerId: string }>();
  const copyResetRef = useRef<number | null>(null);
  const loadingStatusId = useId();
  const contactHeadingId = useId();
  const quickStatsHeadingId = useId();
  const qualificationHeadingId = useId();

  useEffect(() => {
    const fetchResellerData = async () => {
      const { resellerStore } = store!;
      await resellerStore.getReseller(applicationId, resellerId || "");
      await resellerStore.getResellerSalesData(applicationId, resellerId || "");
      setLoading(false);
    };

    void fetchResellerData();
  }, [applicationId, resellerId, store]);

  useEffect(() => {
    return () => {
      if (copyResetRef.current) {
        window.clearTimeout(copyResetRef.current);
      }
    };
  }, []);

  const { resellerStore, manufacturerStore } = store!;
  const { reseller, resellerSalesData } = resellerStore;
  const { manufacturer } = manufacturerStore;

  const ownQualification = reseller?.qualifications?.find(q => q.manufacturerId === manufacturer?.manufacturerId && q.qualificationId !== "Do Not Sell" && q.qualificationId !== "Suspended");
  const otherQualifications = reseller?.qualifications?.filter(q => q.manufacturerId !== manufacturer?.manufacturerId);

  const handleCopyContact = useCallback((label: string, value?: string) => {
    if (!value) return;
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(value).catch(() => { /* no-op */ });
    }
    setCopiedField(label);
    if (copyResetRef.current) {
      window.clearTimeout(copyResetRef.current);
    }
    copyResetRef.current = window.setTimeout(() => setCopiedField(null), 2000);
  }, []);

  if (!reseller) {
    return <Preloader text="Loading Reseller..." color="black" />;
  }

  const location = useMemo(
    () => [reseller.city, reseller.province, reseller.country].filter(Boolean).join(", "),
    [reseller.city, reseller.country, reseller.province]
  );

  const statusBadges = useMemo(() => {
    const statuses: { id: string; label: string; tone: "positive" | "warning" | "neutral"; icon?: ReactNode }[] = [];
    if (reseller.isQualified) {
      statuses.push({
        id: "qualified",
        label: "Qualified",
        tone: "positive",
        icon: <BadgeCheck className="h-4 w-4" aria-hidden="true" />,
      });
    }

    if (reseller.onHold) {
      statuses.push({
        id: "on-hold",
        label: "On hold",
        tone: "warning",
        icon: <AlertTriangle className="h-4 w-4" aria-hidden="true" />,
      });
    } else {
      statuses.push({ id: "status", label: "Active", tone: "neutral" });
    }

    return statuses;
  }, [reseller.isQualified, reseller.onHold]);

  const contactItems = useMemo<ContactItem[]>(() => {
    const items: (ContactItem | undefined)[] = [
      reseller.contactName
        ? {
        label: "Point of contact",
        value: reseller.contactName,
        type: "text",
        }
        : undefined,
      reseller.publicEmail
        ? {
        label: "Email",
        value: reseller.publicEmail,
        type: "link",
        href: `mailto:${reseller.publicEmail}`,
        }
        : undefined,
      reseller.phone
        ? {
        label: "Phone",
        value: reseller.phone,
        type: "action",
        }
        : undefined,
      normalizeUrl(reseller.website)
        ? {
        label: "Website",
        value: normalizeUrl(reseller.website)!,
        type: "link",
        href: normalizeUrl(reseller.website)!,
        }
        : undefined,
    ];

    return items.filter((item): item is ContactItem => Boolean(item));
  }, [reseller.contactName, reseller.phone, reseller.publicEmail, reseller.website]);

  const quickStats = useMemo(() => {
    const activeCount = reseller.activeOrderDetails?.length ?? 0;
    const completeCount = reseller.completedOrderDetails?.length ?? 0;
    const returnsCount = reseller.returns?.length ?? 0;
    const opportunitiesCount = reseller.opportunities?.length ?? 0;
    const numberFormatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });

    return [
      {
        id: sectionIds.activeOrders,
        label: "Active orders",
        value: numberFormatter.format(activeCount),
        helper: activeCount ? "Tap to review open POs" : "No open orders",
        href: activeCount ? `#${sectionIds.activeOrders}` : undefined,
      },
      {
        id: sectionIds.completedOrders,
        label: "Completed orders",
        value: numberFormatter.format(completeCount),
        helper: completeCount ? "Shipped & invoiced" : "Nothing completed yet",
        href: completeCount ? `#${sectionIds.completedOrders}` : undefined,
      },
      {
        id: sectionIds.returns,
        label: "Returns",
        value: numberFormatter.format(returnsCount),
        helper: returnsCount ? "Defectives logged" : "No returns on record",
        href: returnsCount ? `#${sectionIds.returns}` : undefined,
      },
      {
        id: sectionIds.opportunities,
        label: "Opportunities",
        value: numberFormatter.format(opportunitiesCount),
        helper: opportunitiesCount ? "Bids & rebates" : "No bids yet",
        href: opportunitiesCount ? `#${sectionIds.opportunities}` : undefined,
      },
    ];
  }, [reseller.activeOrderDetails, reseller.completedOrderDetails, reseller.opportunities, reseller.returns]);

  const highlightedQualificationCopy = useMemo(() => {
    if (!highlightedQualification) return null;
    const match = reseller.qualifications?.find((q) => q.manufacturerId === highlightedQualification);
    if (!match) return null;
    return `${match.manufacturerPublicName || "Partner"} • ${match.qualificationId || "Qualification"}`;
  }, [highlightedQualification, reseller.qualifications]);

  const primaryAction = (
    <div role="group" aria-label="Reseller actions" className="w-full">
      <ButtonWithActions buttonText="Actions">
        <ResellerActions reseller={reseller} />
      </ButtonWithActions>
    </div>
  );

  return (
    <div className="space-y-6">
      <div
        className="h-1 w-full overflow-hidden rounded-full bg-slate-200"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        id={loadingStatusId}
      >
        <span className="sr-only">{loading ? "Loading reseller data" : "Reseller data loaded"}</span>
        <div
          className={`h-full bg-[var(--ops-accent)] transition-all duration-700 ease-out ${loading ? "animate-pulse w-1/2" : "w-full"}`}
        />
      </div>

      <header
        className="relative overflow-hidden rounded-[var(--surface-radius-xl)] bg-[var(--assistant-prompt-gradient)] p-6 shadow-[var(--shadow-card)]"
        aria-live="polite"
      >
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-[0.3em] text-[var(--ops-text-muted)]">Reseller Profile</p>
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-3xl font-semibold text-slate-900">{reseller.publicName || reseller.name}</h1>
                <div className="flex flex-wrap gap-2">
                  {statusBadges.map((status) => (
                    <span
                      key={status.id}
                      className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold transition-colors duration-200 ${
                        status.tone === "positive"
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                          : status.tone === "warning"
                            ? "border-amber-200 bg-amber-50 text-amber-700"
                            : "border-slate-200 bg-white/70 text-slate-700"
                      }`}
                    >
                      {status.icon}
                      {status.label}
                    </span>
                  ))}
                </div>
              </div>
              {location && <p className="text-sm text-slate-600">{location}</p>}
            </div>

            <dl className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl border border-[var(--ops-border)] bg-white/80 p-4 shadow-sm">
                <dt className="text-xs font-semibold uppercase tracking-wide text-[var(--ops-text-muted)]">Last Invoice</dt>
                <dd className="text-lg font-semibold text-slate-900">{formatDate(reseller.lastInvoiceDate)}</dd>
              </div>
              <div className="rounded-2xl border border-[var(--ops-border)] bg-white/80 p-4 shadow-sm">
                <dt className="text-xs font-semibold uppercase tracking-wide text-[var(--ops-text-muted)]">Joined Randmar</dt>
                <dd className="text-lg font-semibold text-slate-900">{formatDate(reseller.creationDate)}</dd>
              </div>
            </dl>
          </div>
          <div className="flex w-full flex-col gap-4 lg:max-w-md">
            <div
              className="rounded-2xl border border-[var(--ops-border)] bg-white/80 p-4 shadow-sm"
              aria-labelledby={contactHeadingId}
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--ops-text-muted)]" id={contactHeadingId}>
                Contact
              </p>
              <ul className="mt-3 flex flex-wrap gap-2" role="list">
                {contactItems.map((item) => (
                  <li key={item.label} className="flex">
                    {item?.type === "link" ? (
                      <a
                        href={item.href}
                        className="inline-flex items-center rounded-full border border-[var(--ops-border)] bg-white/70 px-4 py-2 text-sm font-medium text-slate-700 transition-all duration-200 hover:-translate-y-0.5 hover:border-[var(--ops-accent)] hover:text-[var(--ops-accent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ops-focus-ring)]"
                      >
                        {item.label}: <span className="ml-1 font-semibold">{item.value}</span>
                      </a>
                    ) : item?.type === "action" ? (
                      <button
                        type="button"
                        onClick={() => handleCopyContact(item.label, item.value)}
                        className={`inline-flex items-center rounded-full border px-4 py-2 text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ops-focus-ring)] ${
                          copiedField === item.label
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                            : "border-[var(--ops-border)] bg-white/70 text-slate-700 hover:-translate-y-0.5 hover:border-[var(--ops-accent)] hover:text-[var(--ops-accent)]"
                        }`}
                        aria-pressed={copiedField === item.label}
                      >
                        {copiedField === item.label ? "Copied phone" : `${item.label}: `}
                        {copiedField !== item.label && <span className="ml-1 font-semibold">{item.value}</span>}
                      </button>
                    ) : (
                      <span className="inline-flex items-center rounded-full border border-[var(--ops-border)] bg-white/70 px-4 py-2 text-sm font-medium text-slate-700">
                        {item?.label}: <span className="ml-1 font-semibold">{item?.value}</span>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
              {contactItems.length === 0 && (
                <p className="mt-2 text-sm text-[var(--ops-text-muted)]">No contact details have been published for this reseller yet.</p>
              )}
              {copiedField && (
                <p className="mt-2 text-xs text-emerald-700" role="status">
                  {copiedField} copied to clipboard
                </p>
              )}
            </div>
            <div className="self-start">{primaryAction}</div>
          </div>
        </div>
      </header>

      <section className="grid gap-6 lg:grid-cols-2" aria-label="Reseller overview">
        <article className="rounded-[var(--surface-radius-xl)] border border-[var(--ops-border)] bg-[var(--ops-card)] p-1 shadow-[var(--shadow-card)]">
          <RampUIResellerCard reseller={reseller} />
        </article>
        <article className="rounded-[var(--surface-radius-xl)] border border-[var(--ops-border)] bg-[var(--ops-card)] p-4 shadow-[var(--shadow-card)]">
          <SalesSummary salesData={reseller.salesData} isQualified={reseller.isQualified} fiscalYearMonthStart={manufacturer?.fiscalYearMonthStart} />
        </article>
      </section>

      <section
        className="rounded-[var(--surface-radius-xl)] border border-[var(--ops-border)] bg-[var(--ops-card)] p-4 shadow-[var(--shadow-card)]"
        aria-labelledby={quickStatsHeadingId}
      >
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--ops-border)] pb-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[var(--ops-text-muted)]">Workflow overview</p>
            <h2 id={quickStatsHeadingId} className="text-xl font-semibold text-slate-900">Pipeline health</h2>
          </div>
          <p className="text-sm text-[var(--ops-text-muted)]">Jump into the sections that need attention</p>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {quickStats.map((stat) =>
            stat.href ? (
              <a
                key={stat.id}
                href={stat.href}
                className="group rounded-2xl border border-[var(--ops-border)] bg-gradient-to-br from-white via-white to-[var(--ops-card-muted)] p-4 text-left shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-[var(--ops-accent)] hover:shadow-[var(--shadow-card)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ops-focus-ring)]"
              >
                <p className="text-xs uppercase tracking-wide text-[var(--ops-text-muted)]">{stat.label}</p>
                <p className="mt-2 text-3xl font-semibold text-slate-900">{stat.value}</p>
                <p className="text-sm text-[var(--ops-text-muted)]">{stat.helper}</p>
              </a>
            ) : (
              <div
                key={stat.id}
                className="rounded-2xl border border-dashed border-[var(--ops-border)] bg-white/60 p-4 opacity-60"
                aria-disabled="true"
              >
                <p className="text-xs uppercase tracking-wide text-[var(--ops-text-muted)]">{stat.label}</p>
                <p className="mt-2 text-3xl font-semibold text-slate-900">{stat.value}</p>
                <p className="text-sm text-[var(--ops-text-muted)]">{stat.helper}</p>
              </div>
            )
          )}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-12" aria-labelledby={qualificationHeadingId}>
        {(reseller.qualifications?.length ?? 0) > 0 ? (
          <Card
            className="col-span-full border border-[var(--ops-border)] bg-[var(--ops-card)] shadow-[var(--shadow-card)] lg:col-span-7"
            id={sectionIds.qualifications}
          >
            <CardHeader className="space-y-1">
              <p className="text-xs uppercase tracking-[0.3em] text-[var(--ops-text-muted)]">Partner fit</p>
              <CardTitle id={qualificationHeadingId}>Qualifications</CardTitle>
              <p className="text-sm text-[var(--ops-text-muted)]">This reseller&apos;s qualification footprint across Randmar partners.</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {ownQualification && (
                <div className="rounded-2xl border border-[var(--ops-border)] bg-white/80 p-4 shadow-sm">
                  <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                    <img
                      className="h-16 w-16 rounded-xl border border-[var(--ops-border)] object-contain p-2"
                      src={`https://api.randmar.io/V4/Partner/${ownQualification.manufacturerId}/Account/Logo?width=100`}
                      alt={`${manufacturer?.publicName || "Manufacturer"} logo`}
                      loading="lazy"
                    />
                    <div>
                      <p className="text-sm text-[var(--ops-text-muted)]">Your brand</p>
                      <p className="text-lg font-semibold text-slate-900">{manufacturer?.publicName}</p>
                      <p className="text-sm text-slate-600">Qualification ID: <span className="font-semibold text-slate-900">{ownQualification.qualificationId}</span></p>
                    </div>
                  </div>
                </div>
              )}
              {otherQualifications && otherQualifications.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-800">Other partners</h3>
                  <ul className="mt-3 flex flex-wrap gap-3" role="list">
                    {otherQualifications.map((row) => (
                      <li key={row.manufacturerId}>
                        <button
                          type="button"
                          onMouseEnter={() => setHighlightedQualification(row.manufacturerId || null)}
                          onFocus={() => setHighlightedQualification(row.manufacturerId || null)}
                          onMouseLeave={() => setHighlightedQualification(null)}
                          onBlur={() => setHighlightedQualification(null)}
                          className="group flex h-16 w-16 items-center justify-center rounded-2xl border border-[var(--ops-border)] bg-white p-2 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-[var(--ops-accent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ops-focus-ring)]"
                          aria-pressed={highlightedQualification === row.manufacturerId}
                        >
                          <img
                            src={`https://api.randmar.io/V4/Partner/${row.manufacturerId}/Account/Logo?width=100`}
                            alt={`${row.manufacturerPublicName || "Manufacturer"} logo`}
                            className="h-full w-full object-contain"
                            loading="lazy"
                          />
                        </button>
                      </li>
                    ))}
                  </ul>
                  {highlightedQualificationCopy && (
                    <p className="mt-2 text-xs text-[var(--ops-text-muted)]" role="status">
                      {highlightedQualificationCopy}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="col-span-full border border-[var(--ops-border)] bg-[var(--ops-card)] shadow-[var(--shadow-card)]" id={sectionIds.qualifications}>
            <CardHeader>
              <CardTitle id={qualificationHeadingId}>Qualifications</CardTitle>
              <p className="text-sm text-[var(--ops-text-muted)]">No qualifications have been recorded for this reseller yet.</p>
            </CardHeader>
          </Card>
        )}
        <Card className="col-span-full border border-[var(--ops-border)] bg-[var(--ops-card)] shadow-[var(--shadow-card)] lg:col-span-5">
          <CardHeader>
            <p className="text-xs uppercase tracking-[0.3em] text-[var(--ops-text-muted)]">Velocity</p>
            <CardTitle>Sales history</CardTitle>
            <p className="text-sm text-[var(--ops-text-muted)]">Trailing 12 months of units sold.</p>
          </CardHeader>
          <CardContent className="pt-0">
            <SalesHistoryChart sales={resellerSalesData || []} />
          </CardContent>
        </Card>
      </section>

      <section className="grid grid-cols-1 gap-5 lg:grid-cols-2" aria-label="Orders and returns">
        {(reseller.activeOrderDetails || []).length > 0 && (
          <Card className="border border-[var(--ops-border)] bg-[var(--ops-card)] shadow-[var(--shadow-card)]" id={sectionIds.activeOrders}>
            <CardHeader>
              <p className="text-xs uppercase tracking-[0.3em] text-[var(--ops-text-muted)]">Live fulfillment</p>
              <CardTitle>Active Orders</CardTitle>
              <p className="text-sm text-[var(--ops-text-muted)]">Refer to Metrics for color scheme</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <OpenOrdersSummary openOrders={reseller.activeOrderDetails} />
              <OpenOrdersGrid productOrders={reseller.activeOrderDetails} />
            </CardContent>
          </Card>
        )}
        {(reseller.completedOrderDetails || []).length > 0 && (
          <Card className="border border-[var(--ops-border)] bg-[var(--ops-card)] shadow-[var(--shadow-card)]" id={sectionIds.completedOrders}>
            <CardHeader>
              <p className="text-xs uppercase tracking-[0.3em] text-[var(--ops-text-muted)]">Fulfilled</p>
              <CardTitle>Completed Orders</CardTitle>
              <p className="text-sm text-[var(--ops-text-muted)]">Shipped and invoiced</p>
            </CardHeader>
            <CardContent>
              <OpenOrdersGrid productOrders={reseller.completedOrderDetails} defaultRowsPerPage={10} />
            </CardContent>
          </Card>
        )}
        {reseller.returns && reseller.returns.length > 0 && (
          <Card className="border border-[var(--ops-border)] bg-[var(--ops-card)] shadow-[var(--shadow-card)]" id={sectionIds.returns}>
            <CardHeader>
              <p className="text-xs uppercase tracking-[0.3em] text-[var(--ops-text-muted)]">Post-sale</p>
              <CardTitle>Returns</CardTitle>
              <p className="text-sm text-[var(--ops-text-muted)]">Defectives</p>
            </CardHeader>
            <CardContent>
              <ReturnGrid returns={reseller.returns} />
            </CardContent>
          </Card>
        )}
        {reseller.opportunities && reseller.opportunities.length > 0 && (
          <Card className="border border-[var(--ops-border)] bg-[var(--ops-card)] shadow-[var(--shadow-card)]" id={sectionIds.opportunities}>
            <CardHeader>
              <p className="text-xs uppercase tracking-[0.3em] text-[var(--ops-text-muted)]">Bids</p>
              <CardTitle>Opportunities</CardTitle>
              <p className="text-sm text-[var(--ops-text-muted)]">Bids and rebates</p>
            </CardHeader>
            <CardContent>
              <OpportunitiesGrid opportunities={reseller.opportunities} />
            </CardContent>
          </Card>
        )}
      </section>
    </div>
  );
});

export default withRoot(ResellerPage);
